import { useRef, useState, useEffect } from 'react';
import { useAssistants } from '../context/AssistantContext';

const BADGE_COLORS = [
  'bg-emerald-500',
  'bg-amber-500',
  'bg-sky-500',
  'bg-violet-500',
  'bg-rose-500',
];

export function AssistantDropdown() {
  const { assistants, activeAssistant, activeAssistantId, setActiveAssistantId } = useAssistants();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const readyAssistants = assistants.filter(a => a.status === 'ready');

  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, []);

  // Guard against -1 (active assistant not in the ready list) so we never
  // index BADGE_COLORS with a negative number.
  const getBadgeColor = (index: number) =>
    BADGE_COLORS[((index % BADGE_COLORS.length) + BADGE_COLORS.length) % BADGE_COLORS.length];

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex items-center gap-2 px-3 py-2 bg-white border border-neutral-200 rounded-btn text-sm text-neutral-700 hover:bg-neutral-50 transition-colors min-w-[200px] w-full"
      >
        {activeAssistant ? (
          <>
            <span
              className={`w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0 ${getBadgeColor(readyAssistants.findIndex(a => a.id === activeAssistant.id))}`}
            >
              {activeAssistant.name.charAt(0).toUpperCase()}
            </span>
            <span className="flex-1 text-left truncate font-medium">{activeAssistant.name}</span>
          </>
        ) : (
          <>
            <span className="w-6 h-6 rounded-full bg-neutral-200 flex items-center justify-center flex-shrink-0">
              <svg className="w-3.5 h-3.5 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </span>
            <span className="flex-1 text-left text-neutral-400">Select Assistant</span>
          </>
        )}
        <svg
          className={`w-4 h-4 text-neutral-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div
          role="listbox"
          aria-label="Select Assistant"
          className="absolute top-full mt-1 left-0 right-0 bg-white border border-neutral-200 rounded-card shadow-card z-10 overflow-hidden"
        >
          {readyAssistants.length === 0 ? (
            <div className="px-4 py-3 text-sm text-neutral-400 text-center">
              No assistants yet. Create one in the Assistants tab.
            </div>
          ) : (
            readyAssistants.map((assistant, index) => (
              <button
                key={assistant.id}
                role="option"
                aria-selected={assistant.id === activeAssistantId}
                onClick={() => {
                  setActiveAssistantId(assistant.id);
                  setOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm text-left transition-colors ${
                  assistant.id === activeAssistantId
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-neutral-700 hover:bg-neutral-50'
                }`}
              >
                <span
                  className={`w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0 ${getBadgeColor(index)}`}
                >
                  {assistant.name.charAt(0).toUpperCase()}
                </span>
                <span className="truncate font-medium">{assistant.name}</span>
                {assistant.id === activeAssistantId && (
                  <svg className="w-4 h-4 ml-auto flex-shrink-0 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
